import { toast } from "sonner";
import { AlertTriangle, Check, ShieldQuestion, X } from "lucide-react";
import { useConnectorAccessRequests } from "@/api/queries/connectorAccess";
import { useApproveAccessRequest, useRejectAccessRequest, useRevokeAccessRequest } from "@/api/mutations/connectorAccess";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SectionCard } from "@/components/SectionCard";
import { relativeTime } from "@/lib/relativeTime";

// #279: Zugriffs-Anträge auf DIESEN Connector — die Sicht, die beim Klick auf den Connector
// erscheint. Der Admin darf alles entscheiden; der Fachverantwortliche (Owner) seine eigenen.
export function AccessRequestsSection({ connectorId }: { connectorId: string }) {
  const { data, isLoading, isError } = useConnectorAccessRequests(connectorId);
  const approve = useApproveAccessRequest();
  const reject = useRejectAccessRequest();
  const revoke = useRevokeAccessRequest();

  const rows = data ?? [];
  const offen = rows.filter((r) => r.status === "pending");
  const busy = approve.isPending || reject.isPending || revoke.isPending;

  function run(p: Promise<unknown>, ok: string) {
    void p.then(
      () => toast.success(ok),
      (e) => toast.error(`Fehlgeschlagen: ${(e as Error).message}`),
    );
  }

  return (
    <SectionCard
      testId="connector-access-requests"
      icon={<ShieldQuestion className="h-[18px] w-[18px]" />}
      title={`Zugriffs-Anfragen (${offen.length} offen)`}
      description="Wer möchte diesen Connector für welchen Agent nutzen? Freigeben schaltet ihn sofort frei."
      noPad
    >
      {isError ? (
        <div className="flex items-center gap-2 p-[18px] text-[12.5px] text-danger" data-testid="access-requests-error">
          <AlertTriangle className="h-4 w-4" />
          Anfragen konnten nicht geladen werden.
        </div>
      ) : isLoading && !data ? (
        <div className="p-[18px] text-[12.5px] text-text3">Lädt…</div>
      ) : rows.length === 0 ? (
        <div className="p-[18px] text-[12.5px] text-text3">
          Keine Anfragen. Ohne Fachverantwortlichen aktivieren Agent-Owner diesen Connector selbst.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agent</TableHead>
              <TableHead>Antragsteller</TableHead>
              <TableHead>Grund</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <span className="text-[12.5px]">{r.agentName ?? r.agentId}</span>
                  {r.agentName ? <div className="mono text-[11px] text-text3">{r.agentId}</div> : null}
                </TableCell>
                <TableCell>
                  <span className={r.requestedByEmail ? "text-[12.5px]" : "mono text-[11px] text-text3"}>
                    {r.requestedByEmail ?? r.requestedByUserId}
                  </span>
                  <div className="text-[11.5px] text-text3">{relativeTime(r.createdAt)}</div>
                </TableCell>
                <TableCell>
                  <span className="text-[12.5px] text-text2">{r.reason || "—"}</span>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={r.status === "approved" ? "active" : r.status === "rejected" ? "disabled" : "enrolled"}
                  >
                    {r.status === "approved" ? "freigegeben" : r.status === "rejected" ? "abgelehnt" : "offen"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {r.status === "pending" ? (
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => run(approve.mutateAsync(r.id), "Zugriff freigegeben")}
                        data-testid={`access-approve-${r.id}`}
                      >
                        <Check className="h-4 w-4" />
                        Freigeben
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => run(reject.mutateAsync(r.id), "Anfrage abgelehnt")}
                        data-testid={`access-reject-${r.id}`}
                      >
                        <X className="h-4 w-4" />
                        Ablehnen
                      </Button>
                    </div>
                  ) : r.status === "approved" ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={busy}
                      onClick={() => run(revoke.mutateAsync(r.id), "Zugriff entzogen")}
                      data-testid={`access-revoke-${r.id}`}
                    >
                      Entziehen
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
}
